/**
 * Sprint P32 · Drillhole interval renderer (3D).
 * One short cylinder per sample interval, coloured by mode
 * (lithology / grade / classification / ore-waste). InstancedMesh so
 * 400+ intervals render in one draw call. `depthTest=false` keeps the
 * trace visible piercing through the semi-transparent block model.
 */
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { worldToScene, isValidCoordinate, pointInBox } from "@/lib/sceneCoords";
import { CLASS_HEX, ORE_HEX, LITHO_HEX, hashHex } from "../utils/colorPalettes";


export function sampleColour(s, mode, palette, cutoff) {
  if (mode === "lithology") {
    const k = String(s.lithology || "").toUpperCase();
    return LITHO_HEX[k] || (k ? hashHex(k) : "#94a3b8");
  }
  if (mode === "classification") {
    return CLASS_HEX[s.classification || "Unclassified"];
  }
  if (mode === "ore_waste") {
    return (s.grade || 0) >= cutoff ? ORE_HEX.ore : ORE_HEX.waste;
  }
  // grade — map onto palette using min/max of the current samples.
  const g = Number(s.grade || 0);
  const g01 = Math.max(0, Math.min(1, g / (palette._max || 1)));
  const i = Math.min(palette.length - 1,
                        Math.floor(g01 * (palette.length - 1)));
  return palette[i];
}


export function DrillholeMesh3D({
  samples, holes, showTraces, origin, verticalExaggeration,
  activeBBox, includeOutliers = true,
  colourMode = "lithology", palette, cutoff = 0.5,
  radius = 6, selectedHoleId = null,
  highlightedInterval = null,     // {hole_id, from, to} — Feb 2026 log↔3D bridge
  onSelectSample = null,
}) {
  // Pre-compute per-hole direction unit vectors from the collar → toe trace
  // so cylinders align with the actual survey deviation (not always vertical).
  const holeAxis = useMemo(() => {
    const map = {};
    if (!holes) return map;
    for (const h of holes) {
      if (!h.x?.length || h.x.length < 2) {
        map[h.hole_id] = new THREE.Vector3(0, -1, 0);   // default: straight down
        continue;
      }
      const n = h.x.length - 1;
      const dx = (h.x[n] - h.x[0]);
      const dy = (h.z[n] - h.z[0]);                     // world Z → scene Y
      const dz = (h.y[n] - h.y[0]);                     // world Y → scene Z
      const v  = new THREE.Vector3(dx, dy, dz);
      if (v.lengthSq() < 1e-6) v.set(0, -1, 0);
      v.normalize();
      map[h.hole_id] = v;
    }
    return map;
  }, [holes]);

  const geometry = useMemo(
    () => new THREE.CylinderGeometry(1, 1, 1, 14, 1, false),
    []);
  const material = useMemo(
    () => new THREE.MeshBasicMaterial({
      transparent: false, side: THREE.DoubleSide,
      depthTest: false, depthWrite: false,
    }),
    []);

  const meshRef = useRef();
  const dummy   = useMemo(() => new THREE.Object3D(), []);
  const upY     = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const quat    = useMemo(() => new THREE.Quaternion(), []);

  useEffect(() => {
    if (!meshRef.current || !samples?.length || !origin) return;
    // Palette max — for grade colouring
    const pal = [...palette]; pal._max = 1;
    if (colourMode === "grade") {
      let m = 0;
      for (const s of samples) if ((s.grade || 0) > m) m = s.grade || 0;
      pal._max = m || 1;
    }
    const colour = new THREE.Color();
    let idx = 0;
    for (let i = 0; i < samples.length; i++) {
      const s = samples[i];
      if (!isValidCoordinate(s.x, s.y, s.z)) continue;
      const p  = worldToScene(s.x, s.y, s.z, origin, verticalExaggeration);
      const dz = Math.max(0.1, (s.length || (s.to - s.from) || 1));
      const scaled = dz * verticalExaggeration;

      if (activeBBox && !includeOutliers && !pointInBox(p, activeBBox)) {
        dummy.position.set(0, 1e6, 0);
        dummy.scale.set(0.0001, 0.0001, 0.0001);
        dummy.updateMatrix();
        meshRef.current.setMatrixAt(idx++, dummy.matrix);
        continue;
      }

      const isSel = selectedHoleId && String(s.hole_id) === String(selectedHoleId);
      // Feb 2026 · interval-highlight bridge: samples that overlap the
      // hole_id + [from, to] window from the URL get a bright cyan flash.
      const isHighlighted = !!(
        highlightedInterval
        && String(s.hole_id) === String(highlightedInterval.hole_id)
        && Number(s.to)   > (highlightedInterval.from ?? -Infinity)
        && Number(s.from) < (highlightedInterval.to   ??  Infinity)
      );
      const r     = isHighlighted ? radius * 2.4 : isSel ? radius * 1.9 : radius;

      const dir = holeAxis[s.hole_id] || upY;
      quat.setFromUnitVectors(upY, dir);

      dummy.position.set(p.x, p.y, p.z);
      dummy.scale.set(r, scaled, r);
      dummy.quaternion.copy(quat);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(idx, dummy.matrix);

      const hex = sampleColour(s, colourMode, pal, cutoff);
      colour.set(isHighlighted ? "#FBBF24" : isSel ? "#22D3EE" : hex);
      meshRef.current.setColorAt(idx, colour);
      idx++;
    }
    meshRef.current.count = idx;
    meshRef.current.instanceMatrix.needsUpdate = true;
    if (meshRef.current.instanceColor) meshRef.current.instanceColor.needsUpdate = true;
  }, [samples, colourMode, cutoff, palette, origin, radius,
       verticalExaggeration, dummy, activeBBox, includeOutliers,
       holeAxis, upY, quat, selectedHoleId, highlightedInterval]);

  if (!showTraces || !samples?.length) return null;
  return (
    <instancedMesh ref={meshRef}
                    args={[geometry, material, samples.length]}
                    frustumCulled={false}
                    renderOrder={3}
                    onClick={(e) => {
                      if (!onSelectSample) return;
                      e.stopPropagation();
                      const s = samples[e.instanceId];
                      if (s) onSelectSample(s);
                    }}
                    castShadow receiveShadow />
  );
}
